import { motion } from 'framer-motion';
import { JOURNAL } from './data';

// JOURNAL — magazine spread. NO carousel. Hero article large left, secondary stack right.
export default function Journal() {
  const [hero, ...rest] = JOURNAL;
  return (
    <div className="bg-[#F2EBDA] text-[#0E0E0E]">
      {/* Masthead */}
      <div className="border-b border-[#0E0E0E]/10">
        <div className="max-w-[1440px] mx-auto px-8 lg:px-12 py-12 flex flex-wrap items-end justify-between gap-6">
          <div>
            <p className="font-mono text-[10px] tracking-[0.4em] uppercase text-[#7B2D26] mb-3">— Section 04 / The journal</p>
            <h1 className="font-serif text-5xl lg:text-7xl leading-[0.95]">Long-form on <span className="italic text-[#7B2D26]">horology</span>.</h1>
          </div>
          <p className="font-mono text-[11px] text-[#0E0E0E]/50 max-w-md leading-relaxed">A few essays a year on watches, makers, and the work of looking after them. No news. No calendar. Just the writing we wish more shops did.</p>
        </div>
      </div>

      {/* Hero article */}
      <motion.article initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="max-w-[1440px] mx-auto px-8 lg:px-12 py-16 grid lg:grid-cols-[1.4fr_1fr] gap-10 lg:gap-16 items-end">
        <a href="#" className="group block">
          <div className="aspect-[5/3] overflow-hidden mb-8"><img src={hero.img} alt={hero.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-1000" loading="lazy" /></div>
          <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#7B2D26] mb-3">— Featured essay &middot; {hero.date}</p>
          <h2 className="font-serif text-4xl lg:text-6xl leading-[0.95] group-hover:italic transition-all">{hero.title}</h2>
          <p className="font-serif text-lg lg:text-xl text-[#0E0E0E]/60 italic mt-4 leading-[1.45]">{hero.sub}</p>
          <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-[#0E0E0E]/45 mt-5">{hero.read} &middot; <span className="text-[#7B2D26]">Read →</span></p>
        </a>
        {/* Author/excerpt bar */}
        <div className="lg:pl-8 lg:border-l border-[#0E0E0E]/15">
          <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#0E0E0E]/45 mb-5">From the essay</p>
          <p className="font-serif text-xl lg:text-2xl leading-[1.4] italic text-[#0E0E0E]/85">&ldquo;The 1518 sat on the bench for six weeks before I touched it. Some watches need to be looked at first &mdash; properly looked at &mdash; before you earn the right to open them.&rdquo;</p>
          <div className="border-t border-[#0E0E0E]/15 mt-8 pt-6 grid grid-cols-3 gap-4 font-mono text-[10px] tracking-[0.2em] uppercase text-[#0E0E0E]/55">
            <div><p className="text-[#7B2D26]">Words</p><p className="mt-1">Henrik V.</p></div>
            <div><p className="text-[#7B2D26]">Photography</p><p className="mt-1">Léa V.</p></div>
            <div><p className="text-[#7B2D26]">Setting</p><p className="mt-1">Geneva</p></div>
          </div>
        </div>
      </motion.article>

      {/* Secondary essays — 3 columns of clean cards, no images dominating */}
      <section className="border-t border-[#0E0E0E]/10">
        <div className="max-w-[1440px] mx-auto px-8 lg:px-12 py-16">
          <p className="font-mono text-[10px] tracking-[0.4em] uppercase text-[#7B2D26] mb-12">— Other essays</p>
          <div className="grid md:grid-cols-3 gap-12">
            {rest.map((j, i) => (
              <motion.a key={j.title} href="#" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }} className="group block">
                <div className="aspect-[4/3] overflow-hidden mb-6"><img src={j.img} alt={j.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-1000" loading="lazy" /></div>
                <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#7B2D26] mb-2">{j.date}</p>
                <h3 className="font-serif text-2xl leading-tight group-hover:italic transition-all">{j.title}</h3>
                <p className="font-serif text-base text-[#0E0E0E]/60 italic mt-2 leading-[1.5]">{j.sub}</p>
                <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-[#0E0E0E]/45 mt-4">{j.read} &middot; <span className="text-[#7B2D26] group-hover:underline">Read →</span></p>
              </motion.a>
            ))}
          </div>
        </div>
      </section>

      {/* Index — table of all essays for archive feel */}
      <section className="bg-[#0E0E0E] text-[#F2EBDA] py-16">
        <div className="max-w-[1100px] mx-auto px-8 lg:px-12">
          <p className="font-mono text-[10px] tracking-[0.4em] uppercase text-[#A88B4F] mb-3">— Archive</p>
          <h2 className="font-serif text-3xl lg:text-5xl mb-10">Every essay we have written, <span className="italic text-[#A88B4F]">in order</span>.</h2>
          <table className="w-full font-mono text-[12px]">
            <thead>
              <tr className="border-b border-[#F2EBDA]/15">
                <th className="text-left py-3 tracking-[0.2em] uppercase text-[#F2EBDA]/45 w-32">Date</th>
                <th className="text-left py-3 tracking-[0.2em] uppercase text-[#F2EBDA]/45">Title</th>
                <th className="text-right py-3 tracking-[0.2em] uppercase text-[#F2EBDA]/45 w-28 hidden md:table-cell">Length</th>
              </tr>
            </thead>
            <tbody>
              {JOURNAL.map(j => (
                <tr key={j.title} className="border-b border-[#F2EBDA]/8 hover:bg-[#F2EBDA]/5 cursor-pointer">
                  <td className="py-4 text-[#A88B4F]">{j.date}</td>
                  <td className="py-4 font-serif text-base">{j.title}</td>
                  <td className="py-4 text-right text-[#F2EBDA]/55 hidden md:table-cell">{j.read}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
